import { useEffect, useState } from "react";
import { Phone, Mail, MessageSquare, MessageCircle, Globe, MapPin, Linkedin, Instagram, Facebook, Twitter, Youtube, Github, Music2, UserPlus, ArrowLeftRight, X, Check } from "lucide-react";
import { api, BACKEND, fileUrl, errMsg } from "../lib/api";

export const FONTS = ["Plus Jakarta Sans", "Manrope", "DM Sans", "Outfit", "Playfair Display", "Lora", "Space Mono", "Fraunces"];

export function useFont(font) {
  useEffect(() => {
    if (!font || font === "Plus Jakarta Sans") return;
    const id = `font-${font.replace(/\s/g, "-")}`;
    if (document.getElementById(id)) return;
    const l = document.createElement("link");
    l.id = id;
    l.rel = "stylesheet";
    l.href = `https://fonts.googleapis.com/css2?family=${font.replace(/\s/g, "+")}:wght@400;600;700&display=swap`;
    document.head.appendChild(l);
  }, [font]);
}

const CONTACT = {
  call: [Phone, "Call", (v) => `tel:${v}`], sms: [MessageSquare, "Text", (v) => `sms:${v}`], email: [Mail, "Email", (v) => `mailto:${v}`],
  whatsapp: [MessageCircle, "WhatsApp", (v) => `https://wa.me/${v.replace(/\D/g, "")}`], website: [Globe, "Website", (v) => v],
  address: [MapPin, "Directions", (v) => `https://maps.google.com/?q=${encodeURIComponent(v)}`],
};
export const CONTACT_KINDS = Object.keys(CONTACT);
const SOCIAL = { linkedin: Linkedin, instagram: Instagram, facebook: Facebook, x: Twitter, youtube: Youtube, github: Github, tiktok: Music2, website: Globe };
export const SOCIAL_KINDS = Object.keys(SOCIAL);

const radius = (s) => ({ rounded: "12px", pill: "999px", square: "2px" }[s] || "12px");

function videoSrc(url = "") {
  const yt = url.match(/(?:youtu\.be\/|v=|embed\/|shorts\/)([\w-]{11})/);
  if (yt) return `https://www.youtube.com/embed/${yt[1]}`;
  const vm = url.match(/vimeo\.com\/(\d+)/);
  return vm ? `https://player.vimeo.com/video/${vm[1]}` : null;
}

function Block({ b, t, page, ctx }) {
  const d = b.data || {};
  const btn = { background: t.primary, color: "#fff", borderRadius: radius(t.button_style) };
  const click = (url) => ctx.onClick?.(b.id, url);
  switch (b.type) {
    case "profile":
      return (
        <div className="text-center -mx-5 -mt-5 mb-2">
          <div className="h-32 bg-cover bg-center" style={{ backgroundImage: d.cover ? `url(${fileUrl(d.cover)})` : `linear-gradient(135deg, ${t.primary}, transparent)` }} />
          <div className="-mt-14 px-5">
            {d.photo ? <img src={fileUrl(d.photo)} alt="" className="w-28 h-28 rounded-full object-cover mx-auto border-4" style={{ borderColor: t.bg_type === "colour" ? t.bg : "#141414" }} />
              : <div className="w-28 h-28 rounded-full mx-auto border-4 grid place-items-center text-3xl font-bold" style={{ background: t.card_bg, borderColor: t.bg }}>{(d.name || "?")[0]}</div>}
            <h1 className="text-2xl font-bold mt-4" data-testid="public-profile-name">{d.name}</h1>
            {(d.job_title || d.company) && <p className="opacity-70 mt-1 text-sm">{[d.job_title, d.company].filter(Boolean).join(" · ")}</p>}
            {d.logo && <img src={fileUrl(d.logo)} alt="" className="h-8 mx-auto mt-4 object-contain" />}
          </div>
        </div>
      );
    case "contact":
      return (
        <div className="grid grid-cols-3 gap-2">
          {(d.items || []).filter((i) => i.value).map((i, k) => {
            const [Icon, label, href] = CONTACT[i.kind] || CONTACT.website;
            return (
              <a key={k} href={href(i.value)} target={i.kind === "website" || i.kind === "address" || i.kind === "whatsapp" ? "_blank" : undefined} rel="noreferrer"
                onClick={() => click(href(i.value))} className="flex flex-col items-center gap-1.5 py-3 text-xs transition-opacity hover:opacity-80"
                style={{ background: t.card_bg, borderRadius: radius(t.button_style) }} data-testid={`contact-${i.kind}-${k}`}>
                <Icon className="h-5 w-5" style={{ color: t.primary }} /> {i.label || label}
              </a>
            );
          })}
        </div>
      );
    case "social":
      return (
        <div className="flex flex-wrap justify-center gap-3">
          {(d.items || []).filter((i) => i.url).map((i, k) => {
            const Icon = SOCIAL[i.network] || Globe;
            return <a key={k} href={i.url} target="_blank" rel="noreferrer" onClick={() => click(i.url)} className="h-11 w-11 grid place-items-center rounded-full hover:scale-105 transition-transform" style={{ background: t.card_bg }} data-testid={`social-${i.network}`}><Icon className="h-5 w-5" /></a>;
          })}
        </div>
      );
    case "link":
      return <a href={d.url} target="_blank" rel="noreferrer" onClick={() => click(d.url)} className="block text-center font-semibold py-3.5 px-5 transition-opacity hover:opacity-90" style={btn} data-testid={`link-block-${b.id}`}>{d.label || "Link"}</a>;
    case "heading":
      return <h2 className="text-xl font-bold text-center pt-2">{d.text}</h2>;
    case "text":
      return <p className="text-sm leading-relaxed opacity-80 whitespace-pre-line text-center">{d.text}</p>;
    case "image":
      if (!d.url) return null;
      const img = <img src={fileUrl(d.url)} alt={d.alt || ""} className="w-full object-cover" style={{ borderRadius: radius(t.button_style) }} />;
      return d.link ? <a href={d.link} target="_blank" rel="noreferrer" onClick={() => click(d.link)}>{img}</a> : img;
    case "gallery":
      return (
        <div className="flex gap-3 overflow-x-auto snap-x snap-mandatory scrollbar-none -mx-5 px-5">
          {(d.images || []).filter(Boolean).map((u, k) => <img key={k} src={fileUrl(u)} alt="" className="snap-center shrink-0 w-[80%] aspect-[4/3] object-cover" style={{ borderRadius: radius(t.button_style) }} />)}
        </div>
      );
    case "video": {
      const src = videoSrc(d.url);
      return src ? <div className="aspect-video overflow-hidden" style={{ borderRadius: radius(t.button_style) }}><iframe src={src} title="video" className="w-full h-full" allow="autoplay; encrypted-media; picture-in-picture" allowFullScreen /></div> : null;
    }
    case "divider":
      return <div style={{ height: Number(d.height || 16) }} className="flex items-center">{d.line !== false && <div className="w-full h-px opacity-20" style={{ background: t.text }} />}</div>;
    case "exchange":
      return <button onClick={() => ctx.onExchange?.()} className="w-full py-3.5 font-semibold flex items-center justify-center gap-2 border transition-opacity hover:opacity-80" style={{ borderColor: t.primary, color: t.text, borderRadius: radius(t.button_style) }} data-testid="exchange-details-btn"><ArrowLeftRight className="h-4 w-4" /> {d.label || "Exchange details"}</button>;
    case "form":
      return <div className="text-center text-xs opacity-50 py-4 border border-dashed" style={{ borderRadius: radius(t.button_style) }}>Forms are coming soon.</div>;
    default:
      return null;
  }
}

export function PageView({ page, org, removeBranding, preview, onExchange, src }) {
  const t = page.theme || {};
  useFont(t.font);
  const bg = t.bg_type === "gradient" ? `linear-gradient(180deg, ${t.bg_gradient?.[0]}, ${t.bg_gradient?.[1]})`
    : t.bg_type === "image" && t.bg_image ? `center/cover url(${fileUrl(t.bg_image)})` : t.bg;
  const onClick = (blockId, url) => { if (!preview) api.post("/public/track/block-click", { page_id: page.id, block_id: blockId, url }).catch(() => {}); };
  const isCard = page.template_type === "business_card" || page.page_kind === "bio";
  return (
    <div className={`${preview ? "min-h-full" : "min-h-screen"} w-full`} style={{ background: bg, color: t.text, fontFamily: `'${t.font || "Plus Jakarta Sans"}', sans-serif` }}>
      <div className="max-w-md mx-auto p-5 pb-28 space-y-4">
        {page.blocks.filter((b) => !b.hidden).map((b) => <Block key={b.id} b={b} t={t} page={page} ctx={{ onClick, onExchange }} />)}
        {!removeBranding && <p className="text-center text-xs opacity-40 pt-6">Made with TapM</p>}
      </div>
      {isCard && (
        <div className={`${preview ? "sticky" : "fixed"} bottom-0 inset-x-0 p-4 pointer-events-none`}>
          <a href={preview ? undefined : `${BACKEND}/api/public/vcard/${page.id}`} className="pointer-events-auto max-w-md mx-auto flex items-center justify-center gap-2 py-3.5 font-semibold shadow-2xl"
            style={{ background: t.primary, color: "#fff", borderRadius: radius(t.button_style) }} data-testid="save-contact-btn">
            <UserPlus className="h-5 w-5" /> Save contact
          </a>
        </div>
      )}
    </div>
  );
}

export function ExchangeModal({ page, org, onClose, src }) {
  const [f, setF] = useState({ name: "", email: "", phone: "", company: "", note: "", consent: false, website: "" });
  const [state, setState] = useState({ busy: false, done: false, err: "" });
  const t = page.theme || {};
  const set = (k) => (e) => setF({ ...f, [k]: e.target.type === "checkbox" ? e.target.checked : e.target.value });
  const submit = async (e) => {
    e.preventDefault();
    setState({ busy: true, err: "" });
    try {
      await api.post(`/public/exchange/${page.id}`, { ...f, email: f.email || null, source_type: src?.type || "page", source_id: src?.id || null });
      setState({ done: true });
    } catch (er) { setState({ err: errMsg(er) }); }
  };
  const ic = "w-full bg-black/30 border border-white/10 rounded-lg px-3.5 py-3 text-sm focus:outline-none focus:border-white/40";
  return (
    <div className="fixed inset-0 bg-black/70 backdrop-blur-sm z-50 flex items-end sm:items-center justify-center p-0 sm:p-4" onClick={onClose}>
      <div className="w-full max-w-md rounded-t-2xl sm:rounded-2xl p-6 rise" style={{ background: t.card_bg || "#1E1E1F", color: t.text }} onClick={(e) => e.stopPropagation()} data-testid="exchange-modal">
        <div className="flex justify-between items-center mb-4"><h3 className="text-lg font-bold">Share your details</h3><button onClick={onClose} data-testid="exchange-close-btn"><X className="h-5 w-5" /></button></div>
        {state.done ? (
          <div className="text-center py-8" data-testid="exchange-success"><Check className="h-10 w-10 mx-auto mb-3" style={{ color: t.primary }} /><p className="font-semibold">Thanks! Your details have been shared.</p></div>
        ) : (
          <form onSubmit={submit} className="space-y-3">
            <input required placeholder="Full name *" value={f.name} onChange={set("name")} className={ic} data-testid="exchange-name-input" />
            <input type="email" placeholder="Email" value={f.email} onChange={set("email")} className={ic} data-testid="exchange-email-input" />
            <input placeholder="Phone" value={f.phone} onChange={set("phone")} className={ic} data-testid="exchange-phone-input" />
            <input placeholder="Company" value={f.company} onChange={set("company")} className={ic} data-testid="exchange-company-input" />
            <textarea placeholder="Note" rows={2} value={f.note} onChange={set("note")} className={ic} data-testid="exchange-note-input" />
            <input tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")} className="hidden" aria-hidden="true" name="website" />
            <label className="flex gap-2.5 items-start text-xs opacity-80"><input type="checkbox" checked={f.consent} onChange={set("consent")} className="mt-0.5" required data-testid="exchange-consent-checkbox" /> {org?.consent_text || "I agree to share my details."}</label>
            {state.err && <p className="text-red-400 text-sm" data-testid="exchange-error">{state.err}</p>}
            <button disabled={state.busy} className="w-full py-3.5 rounded-xl font-semibold disabled:opacity-60" style={{ background: t.primary, color: "#fff" }} data-testid="exchange-submit-btn">{state.busy ? "Sending…" : "Send"}</button>
          </form>
        )}
      </div>
    </div>
  );
}
